import React, { useState, useEffect } from 'react';
import { 
  Palette, Mail, Globe, Settings, Save, Check, X, 
  AlertCircle, Copy, RefreshCw, Trash2, Star, Plus,
  ExternalLink, Upload, CreditCard, DollarSign, Link2, Unlink,
  BarChart3, TrendingUp, Calendar, Phone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Layout from '../../components/Layout';
import UsageDashboard from '../../components/UsageDashboard';
import AnalyticsDashboard from '../../components/AnalyticsDashboard';
import { useAuth } from '../../context/AuthContext';

const API_URL = '';

export default function TenantSettings() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [domains, setDomains] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('branding');
  const [detectedTenantId, setDetectedTenantId] = useState(null);
  
  // Form states
  const [branding, setBranding] = useState({
    site_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#6366F1',
    accent_color: '#8B5CF6',
    dark_mode: false,
    calendar_booking_url: '',
    contact_email: '',
    contact_phone: ''
  });
  
  const [emailConfig, setEmailConfig] = useState({
    provider: 'maileroo',
    from_email: '',
    from_name: '',
    api_key: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: ''
  });
  
  // Dialog states
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [edranSubdomain, setEdranSubdomain] = useState('');
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  const [checkingDns, setCheckingDns] = useState(null);
  const [dnsResults, setDnsResults] = useState({});
  const [showCloudflare, setShowCloudflare] = useState(false);
  const [cloudflareConfig, setCloudflareConfig] = useState({ api_token: '', zone_id: '', domain: '' });
  const [testEmail, setTestEmail] = useState('');
  const [showTestEmail, setShowTestEmail] = useState(false);
  
  // Get tenant ID from user context, detected, or try to auto-detect
  const tenantId = user?.tenant_id || detectedTenantId;

  // Auto-detect tenant on mount if not available from user
  useEffect(() => {
    const detectTenant = async () => {
      if (user?.tenant_id) {
        setDetectedTenantId(user.tenant_id);
        return;
      }
      
      // Try to detect tenant from backend using admin's email domain
      try {
        const response = await fetch(`${API_URL}/api/tenant/detect`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.tenant_id) {
            setDetectedTenantId(data.tenant_id);
            return;
          }
        }
      } catch (err) {
        console.log('Tenant detection error:', err);
      }
      
      // Fallback: For the primary edrano tenant, use the known ID
      // This is temporary until full multi-tenant resolution is implemented
      if (user?.email?.includes('edrano')) {
        setDetectedTenantId('c03c57eb-ef2d-4eaa-99ce-78870f483efb');
      } else {
        // No tenant found, stop loading
        setLoading(false);
      }
    };
    
    if (token) {
      detectTenant();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    if (tenantId) {
      loadSettings();
      loadDomains();
      loadStripeStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenantSettings(data);
        setBranding({
          site_name: data.branding?.site_name || '',
          logo_url: data.branding?.logo_url || '',
          favicon_url: data.branding?.favicon_url || '',
          primary_color: data.branding?.primary_color || '#6366F1',
          accent_color: data.branding?.accent_color || '#8B5CF6',
          dark_mode: data.branding?.dark_mode || false,
          calendar_booking_url: data.branding?.calendar_booking_url || '',
          contact_email: data.branding?.contact_email || '',
          contact_phone: data.branding?.contact_phone || ''
        });
        setEmailConfig(prev => ({
          ...prev,
          provider: data.email?.provider || 'maileroo',
          from_email: data.email?.from_email || '',
          from_name: data.email?.from_name || ''
        }));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe-connect/status/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStripeStatus(await response.json());
      }
    } catch (err) {
      console.error('Failed to load Stripe status:', err);
    }
  };

  const loadDomains = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDomains(data);
        setEdranSubdomain(data.edrano_subdomain || '');
      }
    } catch (err) {
      console.error('Failed to load domains:', err);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(branding)
      });
      
      if (response.ok) {
        alert('Branding saved successfully!');
        loadSettings();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to save branding');
      }
    } catch (err) {
      alert('Error saving branding');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailConfig)
      });
      
      if (response.ok) {
        alert('Email configuration saved successfully!');
        loadSettings();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to save email configuration');
      }
    } catch (err) {
      alert('Error saving email configuration');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: testEmail })
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message);
        setShowTestEmail(false);
        setTestEmail('');
      } else {
        alert(data.detail || 'Failed to send test email');
      }
    } catch (err) {
      alert('Error sending test email');
    }
  };

  const addDomain = async () => {
    if (!newDomain) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ domain: newDomain })
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(`${data.message}\n\nInstructions: ${data.instructions}`);
        setShowAddDomain(false);
        setNewDomain('');
        loadDomains();
      } else {
        alert(data.detail || 'Failed to add domain');
      }
    } catch (err) {
      alert('Error adding domain');
    }
  };

  const verifyDomain = async (domain) => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains/${domain}/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.verified) {
        alert(data.message);
        loadDomains();
      } else {
        alert(`Verification failed: ${data.error}\n\n${data.instructions}`);
      }
    } catch (err) {
      alert('Error verifying domain');
    }
  };

  const removeDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove ${domain}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains/${domain}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadDomains();
      }
    } catch (err) {
      alert('Error removing domain');
    }
  };

  const setPrimaryDomain = async (domain) => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains/${domain}/set-primary`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadDomains();
      }
    } catch (err) {
      alert('Error setting primary domain');
    }
  };

  const saveSubdomain = async () => {
    setSavingSubdomain(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/subdomain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subdomain: edranSubdomain })
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message);
        loadDomains();
      } else {
        alert(data.detail || 'Failed to set subdomain');
      }
    } catch (err) {
      alert('Error setting subdomain');
    }
    setSavingSubdomain(false);
  };

  const checkDnsPropagation = async (domain) => {
    setCheckingDns(domain);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains/${domain}/check-dns`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDnsResults(prev => ({ ...prev, [domain]: data }));
    } catch (err) {
      setDnsResults(prev => ({ ...prev, [domain]: { error: 'Check failed' } }));
    }
    setCheckingDns(null);
  };

  const setupCloudflare = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings/${tenantId}/domains/cloudflare-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(cloudflareConfig)
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setShowCloudflare(false);
        setCloudflareConfig({ api_token: '', zone_id: '', domain: '' });
        // Auto verify after Cloudflare setup
        setTimeout(() => checkDnsPropagation(cloudflareConfig.domain), 3000);
      } else {
        alert(`Cloudflare error: ${data.error}`);
      }
    } catch (err) {
      alert('Error connecting to Cloudflare');
    }
  };

  const connectStripe = async () => {
    try {
      const redirectUri = `${window.location.origin}/admin/portal-settings?stripe_callback=true`;
      
      const response = await fetch(`${API_URL}/api/stripe-connect/oauth/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          redirect_uri: redirectUri
        })
      });
      
      const data = await response.json();
      
      if (data.already_connected) {
        alert('Stripe account is already connected!');
        loadStripeStatus();
      } else if (data.oauth_url) {
        // Redirect to Stripe OAuth
        window.location.href = data.oauth_url;
      }
    } catch (err) {
      alert('Error initiating Stripe connection');
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? You will not be able to receive payments until you reconnect.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/stripe-connect/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tenant_id: tenantId })
      });
      
      if (response.ok) {
        loadStripeStatus();
        alert('Stripe account disconnected');
      }
    } catch (err) {
      alert('Error disconnecting Stripe');
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      tier1: 'bg-slate-100 text-slate-700',
      tier2: 'bg-blue-100 text-blue-700',
      tier3: 'bg-purple-100 text-purple-700'
    };
    return colors[tier] || colors.tier1;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  if (!tenantId) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-8 px-4">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Tenant Settings Not Available</h2>
              <p className="text-slate-500">
                This feature is only available for white-label tenant administrators.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Portal Settings</h1>
            <p className="text-slate-500 mt-1">Customize your white-label portal</p>
          </div>
          {tenantSettings && (
            <div className="flex items-center gap-3">
              <Badge className={getTierBadge(tenantSettings.subscription?.tier)}>
                {tenantSettings.subscription?.tier?.replace('tier', 'Tier ')}
              </Badge>
              <span className="text-sm text-slate-500">
                {tenantSettings.subscription?.platform_fee_percent}% platform fee
              </span>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Usage
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="domains" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Domains
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payments
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Branding Settings</CardTitle>
                <CardDescription>
                  Customize how your portal looks to your clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Site Name</Label>
                    <Input
                      value={branding.site_name}
                      onChange={(e) => setBranding({...branding, site_name: e.target.value})}
                      placeholder="My Agency Portal"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={branding.logo_url}
                      onChange={(e) => setBranding({...branding, logo_url: e.target.value})}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Favicon URL</Label>
                    <Input
                      value={branding.favicon_url}
                      onChange={(e) => setBranding({...branding, favicon_url: e.target.value})}
                      placeholder="https://example.com/favicon.ico"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                        placeholder="#6366F1"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding.accent_color}
                        onChange={(e) => setBranding({...branding, accent_color: e.target.value})}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding.accent_color}
                        onChange={(e) => setBranding({...branding, accent_color: e.target.value})}
                        placeholder="#8B5CF6"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact & Booking Settings */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    Contact & Booking Settings
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Configure how leads can contact you and schedule calls
                  </p>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Contact Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="email"
                          value={branding.contact_email}
                          onChange={(e) => setBranding({...branding, contact_email: e.target.value})}
                          placeholder="contact@yourcompany.com"
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Contact Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="tel"
                          value={branding.contact_phone}
                          onChange={(e) => setBranding({...branding, contact_phone: e.target.value})}
                          placeholder="+1 (555) 123-4567"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <Label>Calendar Booking URL</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add your Calendly, Cal.com, or other booking link. Leads will be redirected here to schedule calls.
                    </p>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="url"
                        value={branding.calendar_booking_url}
                        onChange={(e) => setBranding({...branding, calendar_booking_url: e.target.value})}
                        placeholder="https://calendly.com/your-name"
                        className="pl-10"
                      />
                    </div>
                    {branding.calendar_booking_url && (
                      <a 
                        href={branding.calendar_booking_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline mt-2"
                      >
                        Test booking link <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Preview */}
                <div className="border rounded-lg p-6 bg-slate-50">
                  <h4 className="font-medium mb-4">Preview</h4>
                  <div className="flex items-center gap-4">
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt="Logo" className="h-10 object-contain" />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.accent_color})` }}
                      >
                        {branding.site_name?.charAt(0) || 'P'}
                      </div>
                    )}
                    <span className="font-semibold text-lg">{branding.site_name || 'Your Portal'}</span>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button 
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ backgroundColor: branding.primary_color }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ backgroundColor: branding.accent_color }}
                    >
                      Accent Button
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveBranding} disabled={saving}>
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Branding
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Business Analytics</CardTitle>
                <CardDescription>
                  Track your portal's business performance and trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AnalyticsDashboard tenantId={tenantId} token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage">
            <Card>
              <CardHeader>
                <CardTitle>Usage & Limits</CardTitle>
                <CardDescription>
                  Monitor your resource usage and plan limits
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UsageDashboard tenantId={tenantId} token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle>Email Configuration</CardTitle>
                <CardDescription>
                  Configure how emails are sent from your portal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Email Provider</Label>
                    <Select 
                      value={emailConfig.provider} 
                      onValueChange={(v) => setEmailConfig({...emailConfig, provider: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maileroo">Maileroo</SelectItem>
                        <SelectItem value="sendgrid">SendGrid</SelectItem>
                        <SelectItem value="smtp">Custom SMTP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>From Email</Label>
                    <Input
                      value={emailConfig.from_email}
                      onChange={(e) => setEmailConfig({...emailConfig, from_email: e.target.value})}
                      placeholder="noreply@yourdomain.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>From Name</Label>
                    <Input
                      value={emailConfig.from_name}
                      onChange={(e) => setEmailConfig({...emailConfig, from_name: e.target.value})}
                      placeholder="Your Company"
                    />
                  </div>
                  
                  {emailConfig.provider !== 'smtp' && (
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        value={emailConfig.api_key}
                        onChange={(e) => setEmailConfig({...emailConfig, api_key: e.target.value})}
                        placeholder="Enter your API key"
                      />
                    </div>
                  )}
                </div>

                {/* SMTP Fields */}
                {emailConfig.provider === 'smtp' && (
                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-medium mb-4">SMTP Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>SMTP Host</Label>
                        <Input
                          value={emailConfig.smtp_host}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_host: e.target.value})}
                          placeholder="smtp.yourdomain.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Port</Label>
                        <Input
                          type="number"
                          value={emailConfig.smtp_port}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_port: parseInt(e.target.value)})}
                          placeholder="587"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Username</Label>
                        <Input
                          value={emailConfig.smtp_username}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_username: e.target.value})}
                          placeholder="username"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SMTP Password</Label>
                        <Input
                          type="password"
                          value={emailConfig.smtp_password}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_password: e.target.value})}
                          placeholder="password"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setShowTestEmail(true)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Test Email
                  </Button>
                  <Button onClick={saveEmailConfig} disabled={saving}>
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Email Config
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Domains Tab */}
          <TabsContent value="domains">
            <div className="space-y-6">

            {/* Edrano Subdomain */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" /> Edrano Subdomain
                </CardTitle>
                <CardDescription>
                  Get a free subdomain under edrano.my (e.g., mybrand.edrano.my)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-0">
                    <Input
                      value={edranSubdomain}
                      onChange={(e) => setEdranSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="mybrand"
                      className="rounded-r-none"
                    />
                    <div className="px-3 py-2 bg-slate-100 border border-l-0 rounded-r-md text-sm text-slate-600 whitespace-nowrap">
                      .edrano.my
                    </div>
                  </div>
                  <Button onClick={saveSubdomain} disabled={savingSubdomain}>
                    {savingSubdomain ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-2">Save</span>
                  </Button>
                  {edranSubdomain && (
                    <a href={`https://${edranSubdomain}.edrano.my`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="icon">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                </div>
                {domains?.edrano_subdomain && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Active at {domains.edrano_subdomain}.edrano.my
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Custom Domains */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Custom Domains</CardTitle>
                    <CardDescription>
                      Connect your own domain to your portal
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowCloudflare(true)}>
                      <img src="https://www.cloudflare.com/favicon.ico" alt="" className="w-4 h-4 mr-2" />
                      Cloudflare Setup
                    </Button>
                    {domains?.can_add_more && (
                      <Button onClick={() => setShowAddDomain(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Domain
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 text-sm text-slate-500">
                  Using {domains?.custom_domains?.length || 0} of {domains?.max_custom_domains || 0} custom domains
                </div>

                {/* Custom Domains List */}
                {domains?.custom_domains?.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {domains.custom_domains.map((d) => (
                      <div key={d.domain} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{d.domain}</span>
                              {d.domain === domains.primary_domain && (
                                <Badge className="bg-indigo-100 text-indigo-700"><Star className="w-3 h-3 mr-1" />Primary</Badge>
                              )}
                              {d.verified ? (
                                <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Verified</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => checkDnsPropagation(d.domain)} disabled={checkingDns === d.domain}>
                              <RefreshCw className={`w-4 h-4 mr-1 ${checkingDns === d.domain ? 'animate-spin' : ''}`} />
                              Check DNS
                            </Button>
                            {!d.verified && (
                              <Button variant="outline" size="sm" onClick={() => verifyDomain(d.domain)}>
                                <Check className="w-4 h-4 mr-1" />
                                Verify
                              </Button>
                            )}
                            {d.verified && d.domain !== domains.primary_domain && (
                              <Button variant="outline" size="sm" onClick={() => setPrimaryDomain(d.domain)}>
                                <Star className="w-4 h-4 mr-1" />
                                Primary
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => removeDomain(d.domain)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>

                        {/* DNS Check Results */}
                        {dnsResults[d.domain] && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-md space-y-2">
                            {dnsResults[d.domain].checks?.map((check, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                {check.status === 'pass' ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : check.status === 'fail' ? (
                                  <X className="w-4 h-4 text-red-600" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-amber-600" />
                                )}
                                <span className="font-medium">{check.name}:</span>
                                <span className="text-slate-600">{check.detail}</span>
                              </div>
                            ))}
                            <div className={`text-sm font-medium ${dnsResults[d.domain].propagated ? 'text-green-600' : 'text-amber-600'}`}>
                              {dnsResults[d.domain].propagated ? 'DNS has propagated! You can verify the domain now.' : 'DNS not yet propagated. This can take up to 24 hours.'}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* DNS Setup Instructions */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">DNS Setup Instructions</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Option 1: CNAME Record (Recommended)</p>
                      <div className="bg-white border rounded-md p-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-slate-500">
                              <th className="pb-2">Type</th>
                              <th className="pb-2">Name</th>
                              <th className="pb-2">Target</th>
                              <th className="pb-2">TTL</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="py-1 font-mono">CNAME</td>
                              <td className="py-1 font-mono">@ or www</td>
                              <td className="py-1 font-mono flex items-center gap-2">
                                <span>{domains?.cname_target || 'tenants.edrano.my'}</span>
                                <button 
                                  onClick={() => {navigator.clipboard.writeText(domains?.cname_target || 'tenants.edrano.my')}}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </td>
                              <td className="py-1">Auto / 300</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Option 2: Automated via Cloudflare</p>
                      <p className="text-sm text-slate-500">
                        If you use Cloudflare, click "Cloudflare Setup" above to automatically create the CNAME record.
                      </p>
                    </div>

                    <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                      <li>Add your domain using the "Add Domain" button</li>
                      <li>Configure the CNAME record at your DNS provider (or use Cloudflare automation)</li>
                      <li>Click "Check DNS" to verify propagation</li>
                      <li>Once propagated, click "Verify" to activate the domain</li>
                      <li>SSL certificate will be automatically provisioned</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment Settings</CardTitle>
                <CardDescription>
                  Connect your Stripe account to receive payments from your clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Platform Fee Info */}
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-indigo-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-indigo-800">Platform Fee</h4>
                      <p className="text-sm text-indigo-700 mt-1">
                        Your tier ({tenantSettings?.subscription?.tier?.replace('tier', 'Tier ')}) has a{' '}
                        <strong>{tenantSettings?.subscription?.platform_fee_percent || 0}%</strong> platform fee.
                        This is automatically deducted from each payment.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stripe Connection Status */}
                <div className="border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        stripeStatus?.connected ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        <CreditCard className={`w-6 h-6 ${
                          stripeStatus?.connected ? 'text-green-600' : 'text-slate-400'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Stripe Account</h3>
                        <p className="text-sm text-slate-500">
                          {stripeStatus?.connected 
                            ? stripeStatus?.business_name || 'Connected'
                            : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {stripeStatus?.connected ? (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        Not Connected
                      </Badge>
                    )}
                  </div>

                  {stripeStatus?.connected ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Charges</p>
                          <p className="font-medium flex items-center gap-1">
                            {stripeStatus?.charges_enabled ? (
                              <><Check className="w-4 h-4 text-green-600" /> Enabled</>
                            ) : (
                              <><X className="w-4 h-4 text-red-500" /> Disabled</>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Payouts</p>
                          <p className="font-medium flex items-center gap-1">
                            {stripeStatus?.payouts_enabled ? (
                              <><Check className="w-4 h-4 text-green-600" /> Enabled</>
                            ) : (
                              <><X className="w-4 h-4 text-red-500" /> Disabled</>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Account ID</p>
                          <p className="font-mono text-sm">{stripeStatus?.account_id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Country</p>
                          <p className="font-medium">{stripeStatus?.country?.toUpperCase()}</p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button variant="outline" asChild>
                          <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Stripe Dashboard
                          </a>
                        </Button>
                        <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={disconnectStripe}>
                          <Unlink className="w-4 h-4 mr-2" />
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-slate-600">
                        Connect your Stripe account to start accepting payments from your clients.
                        All payments will be deposited directly to your Stripe account.
                      </p>
                      <Button onClick={connectStripe}>
                        <Link2 className="w-4 h-4 mr-2" />
                        Connect Stripe Account
                      </Button>
                    </div>
                  )}
                </div>

                {/* Payment Flow Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">How payments work</h4>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">1</span>
                      <span>Client makes a payment through your portal</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">2</span>
                      <span>Payment is processed through your connected Stripe account</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">3</span>
                      <span>Platform fee ({tenantSettings?.subscription?.platform_fee_percent || 0}%) is automatically deducted</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">4</span>
                      <span>Remaining funds are deposited to your Stripe balance</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cloudflare DNS Setup Dialog */}
      <Dialog open={showCloudflare} onOpenChange={setShowCloudflare}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="https://www.cloudflare.com/favicon.ico" alt="" className="w-5 h-5" />
              Cloudflare DNS Setup
            </DialogTitle>
            <DialogDescription>
              Automatically create a CNAME record via Cloudflare API
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Domain</Label>
              <Input
                value={cloudflareConfig.domain}
                onChange={(e) => setCloudflareConfig(p => ({...p, domain: e.target.value.toLowerCase()}))}
                placeholder="portal.yourdomain.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Cloudflare API Token</Label>
              <Input
                type="password"
                value={cloudflareConfig.api_token}
                onChange={(e) => setCloudflareConfig(p => ({...p, api_token: e.target.value}))}
                placeholder="Your Cloudflare API token"
              />
              <p className="text-xs text-slate-500">
                Get it from Cloudflare Dashboard {'>'} My Profile {'>'} API Tokens {'>'} Create Token (use "Edit zone DNS" template)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Zone ID</Label>
              <Input
                value={cloudflareConfig.zone_id}
                onChange={(e) => setCloudflareConfig(p => ({...p, zone_id: e.target.value}))}
                placeholder="Your Cloudflare Zone ID"
              />
              <p className="text-xs text-slate-500">
                Found on the domain's Overview page in Cloudflare, right sidebar under "API"
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-700">
              This will create a CNAME record: {cloudflareConfig.domain || 'yourdomain.com'} → tenants.edrano.my
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloudflare(false)}>Cancel</Button>
            <Button 
              onClick={setupCloudflare}
              disabled={!cloudflareConfig.domain || !cloudflareConfig.api_token || !cloudflareConfig.zone_id}
            >
              Create DNS Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Domain Dialog */}
      <Dialog open={showAddDomain} onOpenChange={setShowAddDomain}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Domain</DialogTitle>
            <DialogDescription>
              Enter the domain you want to use for your portal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Domain</Label>
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                placeholder="portal.yourdomain.com"
              />
              <p className="text-xs text-slate-500">
                Enter without http:// or https://
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDomain(false)}>Cancel</Button>
            <Button onClick={addDomain}>Add Domain</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={showTestEmail} onOpenChange={setShowTestEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify your configuration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmail(false)}>Cancel</Button>
            <Button onClick={sendTestEmail}>Send Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
